import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useMotionValue, animate } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useDailyInspiration, type InspirationSource } from '@/hooks';
import { StoryOverlay } from './StoryOverlay';

const sourceConfig: Record<InspirationSource, {
  glowColor: string;
}> = {
  bible: { glowColor: 'rgba(251, 191, 36, 0.4)' },
  quran: { glowColor: 'rgba(52, 211, 153, 0.4)' },
  leadership: { glowColor: 'rgba(59, 130, 246, 0.4)' },
};

const POSITION_STORAGE_KEY = 'yim-inspiration-btn-position-v2';

const getButtonSize = () => {
  if (typeof window === 'undefined') return 56;
  const width = window.innerWidth;
  if (width < 640) return 40;
  if (width < 768) return 48;
  return 56;
};

const EDGE_PADDING = 0;

export function DailyInspirationStory() {
  const { t } = useTranslation();
  const { data: inspiration, isLoading, isError } = useDailyInspiration();
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentSide, setCurrentSide] = useState<'left' | 'right'>('left');
  const [isMounted, setIsMounted] = useState(false);

  const getInitialPosition = useCallback(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const buttonSize = getButtonSize();
    return {
      x: EDGE_PADDING,
      y: window.innerHeight / 2 - buttonSize / 2,
    };
  }, []);

  const initialPos = getInitialPosition();
  const x = useMotionValue(initialPos.x);
  const y = useMotionValue(initialPos.y);

  useEffect(() => {
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const side = parsed.side || 'right';
        const buttonSize = getButtonSize();
        const savedX = side === 'left' ? EDGE_PADDING : window.innerWidth - buttonSize - EDGE_PADDING;
        const maxY = window.innerHeight - buttonSize - EDGE_PADDING;
        const savedY = Math.min(Math.max(EDGE_PADDING, parsed.y), maxY);

        x.set(savedX);
        y.set(savedY);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- restore persisted position on mount
        setCurrentSide(side);
      } catch {
        // Invalid saved position, keep initial
      }
    }

    setIsMounted(true);
  }, [x, y]);

  useEffect(() => {
    const handleResize = () => {
      const buttonSize = getButtonSize();
      const newX = currentSide === 'left' ? EDGE_PADDING : window.innerWidth - buttonSize - EDGE_PADDING;
      const newY = Math.min(y.get(), window.innerHeight - buttonSize - EDGE_PADDING);
      x.set(newX);
      y.set(newY);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentSide, x, y]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);

    const currentX = x.get();
    const currentY = y.get();
    const buttonSize = getButtonSize();
    const buttonCenterX = currentX + buttonSize / 2;
    const screenMiddle = window.innerWidth / 2;

    const side: 'left' | 'right' = buttonCenterX < screenMiddle ? 'left' : 'right';

    const snappedX = side === 'left' ? EDGE_PADDING : window.innerWidth - buttonSize - EDGE_PADDING;
    const boundedY = Math.min(
      Math.max(EDGE_PADDING, currentY),
      window.innerHeight - buttonSize - EDGE_PADDING
    );

    animate(x, snappedX, { type: 'spring', stiffness: 400, damping: 30 });
    animate(y, boundedY, { type: 'spring', stiffness: 400, damping: 30 });

    setCurrentSide(side);
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: snappedX, y: boundedY, side }));
  }, [x, y]);

  if (isError || isLoading || !inspiration || !isMounted) {
    return null;
  }

  const config = sourceConfig[inspiration.source];

  return (
    <>
      {/* Story Circle Button - Draggable & Snaps to edges */}
      <motion.div
        className="fixed z-50 cursor-grab active:cursor-grabbing touch-none"
        style={{ x, y, left: 0, top: 0 }}
        drag
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={{
          left: EDGE_PADDING,
          right: typeof window !== 'undefined' ? window.innerWidth - getButtonSize() - EDGE_PADDING : 0,
          top: EDGE_PADDING,
          bottom: typeof window !== 'undefined' ? window.innerHeight - getButtonSize() - EDGE_PADDING : 0,
        }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        initial={{ opacity: 1, scale: 1 }}
        animate={{ opacity: 1, scale: 1 }}
        whileDrag={{ scale: 1.1, zIndex: 100 }}
      >
        <motion.button
          onClick={() => {
            if (!isDragging) setIsOpen(true);
          }}
          className="group relative"
          whileHover={{ scale: isDragging ? 1 : 1.1 }}
          whileTap={{ scale: isDragging ? 1 : 0.95 }}
        >
          {/* Animated gradient border ring */}
          <motion.div
            className="absolute -inset-1 rounded-full opacity-90"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            style={{
              background: 'conic-gradient(from 0deg, #FBBF24, #1E40AF, #FBBF24, #1E40AF, #FBBF24)',
            }}
          />

          {/* Pulsing glow effect */}
          <motion.div
            className="absolute -inset-2 rounded-full"
            animate={{
              boxShadow: [
                `0 0 20px ${config.glowColor}`,
                `0 0 40px ${config.glowColor}`,
                `0 0 20px ${config.glowColor}`,
              ],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Inner circle */}
          <div className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center shadow-xl">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-amber-400" />
            </motion.div>

            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 rounded-full bg-gradient-to-br from-red-500 to-pink-500 border-[1.5px] sm:border-2 border-white shadow-lg flex items-center justify-center">
              <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-white" />
            </span>
          </div>

          {/* Label on hover */}
          <motion.div
            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap"
            initial={{ opacity: 0, x: 10 }}
            whileHover={{ opacity: 1, x: 0 }}
          >
            <span className="px-3 py-1.5 rounded-full bg-slate-900/90 backdrop-blur-sm text-white text-xs font-semibold shadow-lg border border-white/10">
              {t('components.inspiration.storyLabel')}
            </span>
          </motion.div>
        </motion.button>
      </motion.div>

      {/* Fullscreen Story Modal */}
      <StoryOverlay
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        inspiration={inspiration}
      />
    </>
  );
}

export default DailyInspirationStory;
